/**
 * escoposDeAcesso — helpers puros entre um editor de acesso (uma caixa por OPÇÃO) e o atributo
 * do Cognito que ele grava (escopos separados por espaço): custom:mcp_scopes, custom:dw_scopes…
 * (skill `app-groups`, "atributos de grupo").
 *
 * Uma opção está liberada se o seu escopo está concedido; opções que compartilham o escopo são
 * liberadas juntas — por isso o editor grava ESCOPOS, nunca ids de opção.
 */

/** Uma linha do editor: uma ferramenta MCP, uma fonte do acervo… */
export interface OpcaoDeAcesso {
  /** Identidade estável da opção (nome da ferramenta, chave da fonte). */
  id: string
  /** O que a pessoa vê na lista. */
  rotulo: string
  /** O escopo que a opção exige. */
  escopo: string
  descricao?: string
  /** Agrupamento opcional (o módulo MCP, por exemplo). */
  grupo?: string
  /** Opção que dá acesso sensível — sinalizada na lista. */
  sensivel?: boolean
}

/** Lê um valor de atributo (separado por espaço ou vírgula) como lista. */
export function parseScopes(value: string | null | undefined): string[] {
  return (value ?? '')
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

/** `granted` (ex.: `*`, `unidades:*`, `unidades:read`) cobre `scope`? */
export function scopeCovers(granted: string, scope: string): boolean {
  if (granted === '*' || granted === scope) return true
  if (granted.endsWith(':*')) {
    return scope.startsWith(granted.slice(0, -1))
  }
  return false
}

/** As opções cujo escopo o valor do atributo concede — o que o editor mostra marcado. */
export function opcoesConcedidas(valor: string | null | undefined, opcoes: readonly OpcaoDeAcesso[]): Set<string> {
  const concedidos = parseScopes(valor)
  const marcadas = new Set<string>()
  for (const opcao of opcoes) {
    if (concedidos.some((g) => scopeCovers(g, opcao.escopo))) marcadas.add(opcao.id)
  }
  return marcadas
}

/** O valor do atributo para um conjunto de opções marcadas: os escopos delas, sem repetir, ordenados. */
export function escoposDasOpcoes(marcadas: Iterable<string>, opcoes: readonly OpcaoDeAcesso[]): string {
  const ids = new Set(marcadas)
  const escopos = new Set<string>()
  for (const opcao of opcoes) {
    if (ids.has(opcao.id)) escopos.add(opcao.escopo)
  }
  return [...escopos].sort().join(' ')
}

/** Toda opção que compartilha o escopo de `id` (inclusive ela mesma). */
export function opcoesComMesmoEscopo(id: string, opcoes: readonly OpcaoDeAcesso[]): string[] {
  const escopo = opcoes.find((o) => o.id === id)?.escopo
  if (!escopo) return [id]
  return opcoes.filter((o) => o.escopo === escopo).map((o) => o.id)
}
